import './Chip.scss';
import React, { ReactNode } from 'react';
import clsx from 'clsx';
import Tooltip from '../Tooltip';
import { MdClose } from 'react-icons/md';
import { ITooltipProps } from '../Tooltip/Tooltip';

export interface IChipProps {
    /** Текст */
    children: ReactNode | ReactNode[];
    /** Функция вызываемая при изменении значения */
    onClick?: () => void;
    /** Функция вызываемая при нажатии на крестик */
    onRemove?: () => Promise<void> | void;
    /** Дизейбл. */
    disabled?: boolean;
    /**
     * Размер.
     * @default s
     */
    size?: 'xs' | 's';
    /** Вариант отображения.
     * @default primary
     */
    type?: 'primary' | 'secondary' | 'outline' | 'fill' | 'fill-outline';
    /** Иконка. */
    icon?: ReactNode;
    /**
     * Позиция отображения иконки.
     * @default 'left'
     */
    iconPosition?: 'right' | 'left';
    /** Бейдж. */
    badge?: string;
    /** Максимальная длина строки. */
    maxLength?: number;
    /**
     * Цвет tooltip.
     * @default 'white'
     */
    tooltipBackground?: ITooltipProps['background'];
    /**
     * Всплытие события нажатия.
     * @default false
     */
    isBubble?: boolean;

    /**
     * Возможность выделения текста.
     * @default false
     */
    isSelectableText?: boolean;

    /**
     * data-testid
     */
    testid?: string;

    /**
     * Компонент-кнопка
     */
    isButton?: boolean;
}

const Chip: React.FC<IChipProps> = ({
    children,
    onClick,
    onRemove,
    size = 's',
    type = 'primary',
    icon,
    iconPosition = 'left',
    badge,
    disabled,
    maxLength = 32,
    tooltipBackground = 'white',
    isBubble = false,
    isSelectableText = false,
    testid,
    isButton = true,
}: IChipProps) => {
    const handleClick = (e: React.MouseEvent) => {
        if (!isButton) return;
        e.preventDefault();
        !isBubble && e.stopPropagation();
        onClick && onClick();
    };

    const handleRemove = async (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove && !disabled && (await onRemove());
    };

    // -------------------------------------------------------------------------------------------------------------------

    const clickableClass = isButton && onClick && !disabled ? 'rf-chip--clickable' : '';

    // -------------------------------------------------------------------------------------------------------------------

    const selectableTextClass = isSelectableText ? 'rf-chip--selectableText' : '';

    // -------------------------------------------------------------------------------------------------------------------

    const overMaxLength = typeof children === 'string' && children.length > maxLength;

    // -------------------------------------------------------------------------------------------------------------------

    const WrapperComponent = ({ children }: { children: ReactNode }) => {
        return isButton ? (
            <button
                type="button"
                className={clsx('rf-chip-wrapper', 'rf-chip-wrapper--button', selectableTextClass)}
                disabled={disabled}
                data-testid={testid}
            >
                {children}
            </button>
        ) : (
            <span
                className={clsx('rf-chip-wrapper', 'rf-chip-wrapper--span', selectableTextClass)}
                data-testid={testid}
            >
                {children}
            </span>
        );
    };

    return (
        <Tooltip background={tooltipBackground} position={'bottom'} isVisible={overMaxLength}>
            <WrapperComponent>
                <div
                    className={clsx(
                        'rf-chip',
                        `rf-chip--${type}`,
                        `rf-chip--${size}`,
                        disabled && 'rf-chip--disabled',
                        clickableClass,
                    )}
                    onClick={handleClick}
                >
                    {!!icon && iconPosition === 'left' && (
                        <div className={clsx('rf-chip__icon', 'rf-chip__icon--left')}>{icon}</div>
                    )}
                    {overMaxLength ? children.slice(0, maxLength) + '...' : children}
                    {onRemove && (
                        <button
                            className={clsx('rf-chip__icon', 'rf-chip__icon--right')}
                            onClick={handleRemove}
                            disabled={disabled}
                            type="button"
                        >
                            <MdClose size={size === 's' ? '18' : '12'} />
                        </button>
                    )}
                    {!!icon && iconPosition === 'right' && (
                        <div className={clsx('rf-chip__icon', 'rf-chip__icon--right')}>{icon}</div>
                    )}
                    {!!badge && (
                        <div className="rf-chip__badge">
                            <span className="rf-chip__badge-text">{badge}</span>
                        </div>
                    )}
                </div>
            </WrapperComponent>
            {children}
        </Tooltip>
    );
};

export default Chip;
